import React, { useEffect, useMemo, useState } from "react";
import { workforceApi } from "../../lib/workforce";
import { Zap, DollarSign, Award, Rocket, Search } from "lucide-react";

/**
 * BrainSelector — grouped-by-family cards.
 * Props:
 *   value: brainId
 *   onChange: (brainId) => void
 *   compact: bool
 */
const TIER_META = {
  premium:  { icon: Award,   label: "Premium",  color: "#8B5CF6" },
  balanced: { icon: Rocket,  label: "Balanced", color: "#0EA5E9" },
  fast:     { icon: Zap,     label: "Fast",     color: "#10B981" },
};

export default function BrainSelector({ value, onChange, compact = false }) {
  const [brains, setBrains] = useState([]);
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("all");

  useEffect(() => {
    workforceApi.listBrains().then((d) => setBrains(d.brains || [])).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    const filtered = brains.filter((b) => {
      if (tier !== "all" && b.tier !== tier) return false;
      if (q && !`${b.family} ${b.name} ${b.blurb}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
    const map = {};
    filtered.forEach((b) => {
      (map[b.family] = map[b.family] || []).push(b);
    });
    return map;
  }, [brains, q, tier]);

  return (
    <div className="space-y-4" data-testid="brain-selector">
      {!compact && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search brains…"
              className="brut-input pl-9 py-2 text-sm"
              data-testid="brain-search"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {["all", "premium", "balanced", "fast"].map((t) => (
              <button
                key={t}
                onClick={() => setTier(t)}
                data-testid={`tier-${t}`}
                className={`px-2.5 py-1.5 rounded-full text-xs font-heading font-bold border-2 transition-all ${
                  tier === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"
                }`}
              >
                {t === "all" ? "All" : t[0].toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        </div>
      )}

      {Object.entries(grouped).map(([family, list]) => (
        <div key={family}>
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">{family}</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {list.map((b) => {
              const selected = value === b.id;
              const TierIcon = TIER_META[b.tier]?.icon || Rocket;
              return (
                <button
                  key={b.id}
                  type="button"
                  data-testid={`brain-${b.id}`}
                  onClick={() => onChange(b.id)}
                  className="text-left rounded-xl border-2 p-3 transition-all"
                  style={{
                    backgroundColor: selected ? b.color + "18" : "#fff",
                    borderColor: selected ? b.color : "#CBD5E1",
                    boxShadow: selected ? `3px 3px 0 ${b.color}` : "none",
                    transform: selected ? "translate(-1px,-1px)" : "none",
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-heading font-bold text-sm" style={{ color: b.color }}>{b.name}</div>
                    <span
                      className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: TIER_META[b.tier]?.color + "22", color: TIER_META[b.tier]?.color }}
                    >
                      {TIER_META[b.tier]?.label}
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 leading-snug">{b.blurb}</div>
                  <div className="flex items-center gap-3 mt-2 text-[10px] font-bold text-slate-500">
                    <span>{(b.context / 1000).toFixed(0)}K ctx</span>
                    <span className="flex items-center gap-0.5">
                      <DollarSign size={10} strokeWidth={3} />
                      {b.cost_in === 0 ? "free" : `${b.cost_in.toFixed(4)}/1K in`}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {Object.keys(grouped).length === 0 && (
        <div className="text-center py-8 text-sm text-slate-500 italic">No brains match your filter.</div>
      )}
    </div>
  );
}
